/**
 * useEditGate — the ONE shared "view-then-edit" pattern for CONFIG PANELS
 * (Settings → Default Settings, App Config, Form Config, Audit Trail
 * Configuration, Email Alerts, …).  It is the config-panel analogue of the
 * list→detail `SettingsDetailPage` (which already opens a record READ-ONLY and
 * unlocks on Edit): a single-page settings form opens READ-ONLY, an **Edit**
 * button unlocks the controls, **Save** persists everything to the DB in ONE
 * audited batch through the shared `useSaveFlow` (confirm → audited handler →
 * "Saved successfully."), and **Cancel** reverts to the loaded snapshot.
 *
 * This gives every config panel the SAME behaviour "like other forms" without
 * each one re-implementing an edit flag + per-control saves.  Pair it with the
 * shared <EditGateActions> (components/AppToolbar) for the standard
 * Edit / Save+Cancel buttons in the toolbar's left slot.
 *
 *   const gate = useEditGate();
 *   // controls: disabled={gate.readOnly}
 *   // toolbar left: <EditGateActions gate={gate}
 *   //     onSave={() => gate.save({ formName:'App Config', save: persistAll, reload })}
 *   //     onCancel={() => gate.cancel(revertToLoaded)} />
 */
import { useCallback, useState } from 'react';
import { useSaveFlow } from './useSaveFlow';

export interface EditGateSaveOptions {
  /** The actual persist — call the existing PUT/command here (one batch). */
  save: () => Promise<void> | void;
  /** Audit form name (dbo.AuditData.FormName) — gates the reason-for-change
   *  prompt on edit when the form has ReasonRequired ON (standing rule 11). */
  formName?: string;
  /** Success acknowledgement (default: the shared "Saved successfully."). */
  successMessage?: string;
  /** Re-fetch after a successful save so the panel reflects the persisted state. */
  reload?: () => void | Promise<void>;
  /** Custom failure handler (defaults to the shared error handling). */
  onError?: (e: unknown) => void;
}

export interface EditGate {
  /** True while the panel is unlocked for editing. */
  editing: boolean;
  /** Convenience inverse — pass to every control's `disabled`/`readOnly`. */
  readOnly: boolean;
  /** Enter edit mode (the toolbar Edit button). */
  beginEdit: () => void;
  /** Leave edit mode WITHOUT saving; pass a revert fn to restore loaded values. */
  cancel: (revert?: () => void) => void;
  /** Persist via the shared save flow; on success, drop back to read-only. */
  save: (opts: EditGateSaveOptions) => Promise<boolean>;
}

export function useEditGate(): EditGate {
  const runSave = useSaveFlow();
  const [editing, setEditing] = useState(false);

  const beginEdit = useCallback(() => setEditing(true), []);

  const cancel = useCallback((revert?: () => void) => {
    revert?.();
    setEditing(false);
  }, []);

  const save = useCallback(
    async (opts: EditGateSaveOptions): Promise<boolean> => {
      const ok = await runSave({
        formName: opts.formName,
        auditAction: 'edit',
        successMessage: opts.successMessage,
        save: opts.save,
        onError: opts.onError,
        onSaved: () => { void opts.reload?.(); },
      });
      if (ok) setEditing(false);
      return ok;
    },
    [runSave],
  );

  return { editing, readOnly: !editing, beginEdit, cancel, save };
}
